import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BrainTrainingGameIcon, GameShell, randomItem, shuffle } from '../shared';
import brainGameLevels from '../../../lib/brainGameLevels';

const BLINK_LETTERS = 'ABCDEFGHJKLMNPQRSTUVWXYZ'.split('');
const BLINK_DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
const { normalizeBrainGameLevel } = brainGameLevels;

const createBlinkRound = (level = 1) => {
    const difficultyIndex = level - 1;
    const targets = shuffle(BLINK_DIGITS).slice(0, 2);
    const firstIndex = 4 + Math.floor(Math.random() * 3);
    const lag = 2 + Math.floor(Math.random() * 4);
    const stream = Array.from({ length: 17 }, () => randomItem(BLINK_LETTERS));
    stream[firstIndex] = targets[0];
    stream[firstIndex + lag] = targets[1];

    return {
        frameMs: Math.max(96, 138 - difficultyIndex * 3),
        id: `${Date.now()}-${Math.random()}`,
        stream,
        targets,
    };
};

function FocusBlinkGame({ copy = {}, initialLevel, ...props }) {
    const startingLevel = normalizeBrainGameLevel('focus-blink', initialLevel);
    const intervalRef = useRef(null);
    const feedbackTimerRef = useRef(null);
    const [round, setRound] = useState(() => createBlinkRound(startingLevel));
    const [stage, setStage] = useState('ready');
    const [frameIndex, setFrameIndex] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const correct = stage === 'feedback' && answers.join('-') === round.targets.join('-');
    const digitOptions = useMemo(() => shuffle(BLINK_DIGITS), [round.id]);

    useEffect(() => () => {
        if (intervalRef.current) {
            window.clearInterval(intervalRef.current);
        }
        if (feedbackTimerRef.current) {
            window.clearTimeout(feedbackTimerRef.current);
        }
    }, []);

    useEffect(() => {
        if (stage !== 'ready') {
            return undefined;
        }

        feedbackTimerRef.current = window.setTimeout(() => play(), 420);
        return () => window.clearTimeout(feedbackTimerRef.current);
    }, [round.id, stage]);

    const play = () => {
        if (stage === 'playing') {
            return;
        }

        if (intervalRef.current) {
            window.clearInterval(intervalRef.current);
        }
        setAnswers([]);
        setFrameIndex(0);
        setStage('playing');

        let nextIndex = 0;
        intervalRef.current = window.setInterval(() => {
            nextIndex += 1;
            if (nextIndex >= round.stream.length) {
                window.clearInterval(intervalRef.current);
                intervalRef.current = null;
                setStage('first');
                return;
            }
            setFrameIndex(nextIndex);
        }, round.frameMs);
    };

    const chooseDigit = (digit) => {
        if (stage !== 'first' && stage !== 'second') {
            return;
        }

        const nextAnswers = [...answers, digit];
        setAnswers(nextAnswers);

        if (nextAnswers.length === 1) {
            setStage('second');
            return;
        }

        const isCorrect = nextAnswers.join('-') === round.targets.join('-');
        const nextStreak = isCorrect ? streak + 1 : 0;
        const nextLevel = normalizeBrainGameLevel('focus-blink', startingLevel + nextStreak);
        setScore((current) => current + (isCorrect ? 1 : 0));
        setStreak(nextStreak);
        setStage('feedback');

        feedbackTimerRef.current = window.setTimeout(() => {
            setRound(createBlinkRound(nextLevel));
            setAnswers([]);
            setFrameIndex(0);
            setStage('ready');
        }, isCorrect ? 560 : 860);
    };

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={{ level: normalizeBrainGameLevel('focus-blink', startingLevel + streak), score, streak }}
            tone={correct ? 'good' : stage === 'feedback' ? 'miss' : 'ready'}
        >
            <div className="UFOV-brain-games__blink-game">
                <div className="UFOV-brain-games__game-cue">
                    {stage === 'ready' || stage === 'playing' ? copy.focusBlinkRememberCue : copy.focusBlinkEnterCue}
                </div>
                {stage === 'ready' || stage === 'playing' ? (
                    <button
                        aria-label={copy.playRapidStream}
                        className={`UFOV-brain-games__blink-stream ${stage === 'playing' ? 'is-playing' : ''}`.trim()}
                        disabled={stage === 'playing'}
                        onClick={play}
                        type="button"
                    >
                        {stage === 'playing' ? round.stream[frameIndex] : <BrainTrainingGameIcon icon="play" />}
                    </button>
                ) : (
                    <>
                        <div className={`UFOV-brain-games__blink-slots ${stage === 'feedback' ? (correct ? 'is-correct' : 'is-wrong') : ''}`.trim()}>
                            {(stage === 'feedback' && !correct ? round.targets : [answers[0] || '?', answers[1] || '?']).map((value, index) => (
                                <span key={`${value}-${index}`}>{value}</span>
                            ))}
                        </div>
                        <div className="UFOV-brain-games__blink-options">
                            {digitOptions.map((digit) => {
                                const answerIndex = answers.indexOf(digit);
                                const isPicked = answerIndex >= 0;
                                const isFeedback = stage === 'feedback';
                                const isCorrect = isFeedback && (
                                    (isPicked && round.targets[answerIndex] === digit)
                                    || (!isPicked && round.targets.includes(digit))
                                );
                                const isWrong = isFeedback && isPicked && round.targets[answerIndex] !== digit;

                                return (
                                    <button
                                        className={`${isPicked ? 'is-picked' : ''} ${isCorrect ? 'is-correct' : ''} ${isWrong ? 'is-wrong' : ''}`.trim()}
                                        disabled={isFeedback || isPicked}
                                        key={digit}
                                        onClick={() => chooseDigit(digit)}
                                        type="button"
                                    >
                                        {digit}
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </GameShell>
    );
}

export default FocusBlinkGame;
